'use client'

import { useMemo, useState } from 'react'
import { AlertCircle, Search, Volume2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { PronunciationScript } from '@/content/pronunciation'
import { getSoundSample } from '@/lib/pronunciation/sound-samples'
import { cn } from '@/lib/utils'
import { PronunciationAudioButton } from './pronunciation-audio-button'

export type ChartSymbol = {
  soundTag: string
  pinyin: string
  zhuyin: string
}

type ChartGroup = {
  symbols: ChartSymbol[]
}

type ChartView = 'full' | 'initials' | 'finals'

// Zhuyin Keyboard/Reference Rows (37 standard Bopomofo symbols)
const ZHUYIN_INITIALS_GROUPS: ChartGroup[] = [
  {
    symbols: [
      { soundTag: 'initial-b', pinyin: 'b', zhuyin: 'ㄅ' },
      { soundTag: 'initial-p', pinyin: 'p', zhuyin: 'ㄆ' },
      { soundTag: 'initial-m', pinyin: 'm', zhuyin: 'ㄇ' },
      { soundTag: 'initial-f', pinyin: 'f', zhuyin: 'ㄈ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-d', pinyin: 'd', zhuyin: 'ㄉ' },
      { soundTag: 'initial-t', pinyin: 't', zhuyin: 'ㄊ' },
      { soundTag: 'initial-n', pinyin: 'n', zhuyin: 'ㄋ' },
      { soundTag: 'initial-l', pinyin: 'l', zhuyin: 'ㄌ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-g', pinyin: 'g', zhuyin: 'ㄍ' },
      { soundTag: 'initial-k', pinyin: 'k', zhuyin: 'ㄎ' },
      { soundTag: 'initial-h', pinyin: 'h', zhuyin: 'ㄏ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-j', pinyin: 'j', zhuyin: 'ㄐ' },
      { soundTag: 'initial-q', pinyin: 'q', zhuyin: 'ㄑ' },
      { soundTag: 'initial-x', pinyin: 'x', zhuyin: 'ㄒ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-zh', pinyin: 'zh', zhuyin: 'ㄓ' },
      { soundTag: 'initial-ch', pinyin: 'ch', zhuyin: 'ㄔ' },
      { soundTag: 'initial-sh', pinyin: 'sh', zhuyin: 'ㄕ' },
      { soundTag: 'initial-r', pinyin: 'r', zhuyin: 'ㄖ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-z', pinyin: 'z', zhuyin: 'ㄗ' },
      { soundTag: 'initial-c', pinyin: 'c', zhuyin: 'ㄘ' },
      { soundTag: 'initial-s', pinyin: 's', zhuyin: 'ㄙ' }
    ]
  }
]

const ZHUYIN_FINALS_GROUPS: ChartGroup[] = [
  {
    symbols: [
      { soundTag: 'final-a', pinyin: 'a', zhuyin: 'ㄚ' },
      { soundTag: 'final-o', pinyin: 'o', zhuyin: 'ㄛ' },
      { soundTag: 'final-e', pinyin: 'e', zhuyin: 'ㄜ' },
      { soundTag: 'final-ie', pinyin: 'ie', zhuyin: 'ㄝ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-ai', pinyin: 'ai', zhuyin: 'ㄞ' },
      { soundTag: 'final-ei', pinyin: 'ei', zhuyin: 'ㄟ' },
      { soundTag: 'final-ao', pinyin: 'ao', zhuyin: 'ㄠ' },
      { soundTag: 'final-ou', pinyin: 'ou', zhuyin: 'ㄡ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-an', pinyin: 'an', zhuyin: 'ㄢ' },
      { soundTag: 'final-en', pinyin: 'en', zhuyin: 'ㄣ' },
      { soundTag: 'final-ang', pinyin: 'ang', zhuyin: 'ㄤ' },
      { soundTag: 'final-eng', pinyin: 'eng', zhuyin: 'ㄥ' },
      { soundTag: 'final-er', pinyin: 'er', zhuyin: 'ㄦ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-i', pinyin: 'i', zhuyin: 'ㄧ' },
      { soundTag: 'final-u', pinyin: 'u', zhuyin: 'ㄨ' },
      { soundTag: 'final-ü', pinyin: 'ü', zhuyin: 'ㄩ' }
    ]
  }
]

// Pinyin Keyboard/Reference Rows
const PINYIN_INITIALS_GROUPS: ChartGroup[] = [
  {
    symbols: [
      { soundTag: 'initial-b', pinyin: 'b', zhuyin: 'ㄅ' },
      { soundTag: 'initial-p', pinyin: 'p', zhuyin: 'ㄆ' },
      { soundTag: 'initial-m', pinyin: 'm', zhuyin: 'ㄇ' },
      { soundTag: 'initial-f', pinyin: 'f', zhuyin: 'ㄈ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-d', pinyin: 'd', zhuyin: 'ㄉ' },
      { soundTag: 'initial-t', pinyin: 't', zhuyin: 'ㄊ' },
      { soundTag: 'initial-n', pinyin: 'n', zhuyin: 'ㄋ' },
      { soundTag: 'initial-l', pinyin: 'l', zhuyin: 'ㄌ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-g', pinyin: 'g', zhuyin: 'ㄍ' },
      { soundTag: 'initial-k', pinyin: 'k', zhuyin: 'ㄎ' },
      { soundTag: 'initial-h', pinyin: 'h', zhuyin: 'ㄏ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-j', pinyin: 'j', zhuyin: 'ㄐ' },
      { soundTag: 'initial-q', pinyin: 'q', zhuyin: 'ㄑ' },
      { soundTag: 'initial-x', pinyin: 'x', zhuyin: 'ㄒ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-zh', pinyin: 'zh', zhuyin: 'ㄓ' },
      { soundTag: 'initial-ch', pinyin: 'ch', zhuyin: 'ㄔ' },
      { soundTag: 'initial-sh', pinyin: 'sh', zhuyin: 'ㄕ' },
      { soundTag: 'initial-r', pinyin: 'r', zhuyin: 'ㄖ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'initial-z', pinyin: 'z', zhuyin: 'ㄗ' },
      { soundTag: 'initial-c', pinyin: 'c', zhuyin: 'ㄘ' },
      { soundTag: 'initial-s', pinyin: 's', zhuyin: 'ㄙ' }
    ]
  }
]

const PINYIN_FINALS_GROUPS: ChartGroup[] = [
  {
    symbols: [
      { soundTag: 'final-a', pinyin: 'a', zhuyin: 'ㄚ' },
      { soundTag: 'final-o', pinyin: 'o', zhuyin: 'ㄛ' },
      { soundTag: 'final-e', pinyin: 'e', zhuyin: 'ㄜ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-ai', pinyin: 'ai', zhuyin: 'ㄞ' },
      { soundTag: 'final-ei', pinyin: 'ei', zhuyin: 'ㄟ' },
      { soundTag: 'final-ao', pinyin: 'ao', zhuyin: 'ㄠ' },
      { soundTag: 'final-ou', pinyin: 'ou', zhuyin: 'ㄡ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-an', pinyin: 'an', zhuyin: 'ㄢ' },
      { soundTag: 'final-en', pinyin: 'en', zhuyin: 'ㄣ' },
      { soundTag: 'final-ang', pinyin: 'ang', zhuyin: 'ㄤ' },
      { soundTag: 'final-eng', pinyin: 'eng', zhuyin: 'ㄥ' },
      { soundTag: 'final-er', pinyin: 'er', zhuyin: 'ㄦ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-i', pinyin: 'i', zhuyin: 'ㄧ' },
      { soundTag: 'final-u', pinyin: 'u', zhuyin: 'ㄨ' },
      { soundTag: 'final-ü', pinyin: 'ü', zhuyin: 'ㄩ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-ia', pinyin: 'ia', zhuyin: 'ㄧㄚ' },
      { soundTag: 'final-ie', pinyin: 'ie', zhuyin: 'ㄧㄝ' },
      { soundTag: 'final-iao', pinyin: 'iao', zhuyin: 'ㄧㄠ' },
      { soundTag: 'final-iu', pinyin: 'iu', zhuyin: 'ㄧㄡ' },
      { soundTag: 'final-ian', pinyin: 'ian', zhuyin: 'ㄧㄢ' },
      { soundTag: 'final-iang', pinyin: 'iang', zhuyin: 'ㄧㄤ' },
      { soundTag: 'final-in', pinyin: 'in', zhuyin: 'ㄧㄣ' },
      { soundTag: 'final-ing', pinyin: 'ing', zhuyin: 'ㄧㄥ' },
      { soundTag: 'final-iong', pinyin: 'iong', zhuyin: 'ㄩㄥ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-ua', pinyin: 'ua', zhuyin: 'ㄨㄚ' },
      { soundTag: 'final-uo', pinyin: 'uo', zhuyin: 'ㄨㄛ' },
      { soundTag: 'final-uai', pinyin: 'uai', zhuyin: 'ㄨㄞ' },
      { soundTag: 'final-ui', pinyin: 'ui', zhuyin: 'ㄨㄟ' },
      { soundTag: 'final-uan', pinyin: 'uan', zhuyin: 'ㄨㄢ' },
      { soundTag: 'final-uang', pinyin: 'uang', zhuyin: 'ㄨㄤ' },
      { soundTag: 'final-un', pinyin: 'un', zhuyin: 'ㄨㄣ' },
      { soundTag: 'final-ong', pinyin: 'ong', zhuyin: 'ㄨㄥ' }
    ]
  },
  {
    symbols: [
      { soundTag: 'final-üe', pinyin: 'üe', zhuyin: 'ㄩㄝ' },
      { soundTag: 'final-üan', pinyin: 'üan', zhuyin: 'ㄩㄢ' },
      { soundTag: 'final-ün', pinyin: 'ün', zhuyin: 'ㄩㄣ' }
    ]
  }
]

type PronunciationChartProps = {
  script: PronunciationScript
  symbols?: unknown // Keeping for backward compatibility (ignored)
}

export function PronunciationChart({ script }: PronunciationChartProps) {
  const [view, setView] = useState<ChartView>('full')
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLowerCase()

  const initialsGroups = useMemo(() => {
    return script === 'pinyin' ? PINYIN_INITIALS_GROUPS : ZHUYIN_INITIALS_GROUPS
  }, [script])

  const finalsGroups = useMemo(() => {
    return script === 'pinyin' ? PINYIN_FINALS_GROUPS : ZHUYIN_FINALS_GROUPS
  }, [script])

  const allSymbols = useMemo(() => {
    const list: ChartSymbol[] = []
    for (const group of [...initialsGroups, ...finalsGroups]) {
      list.push(...group.symbols)
    }
    return list
  }, [initialsGroups, finalsGroups])

  const searchResults = useMemo(() => {
    if (!normalizedQuery) return []
    return allSymbols.filter((symbol) => {
      const matchQuery = query.trim()
      return (
        symbol.pinyin.toLowerCase().includes(normalizedQuery) ||
        symbol.zhuyin.includes(matchQuery)
      )
    })
  }, [allSymbols, normalizedQuery, query])

  const isSearching = !!normalizedQuery

  return (
    <div className="border-muted/50 bg-background/95 space-y-6 rounded-2xl border p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="bg-muted/40 flex self-start rounded-xl p-1">
          {(['full', 'initials', 'finals'] as const).map((candidate) => (
            <Button
              key={candidate}
              type="button"
              size="sm"
              variant={view === candidate ? 'secondary' : 'ghost'}
              onClick={() => setView(candidate)}
              className="rounded-lg capitalize transition-all"
            >
              {candidate === 'full' ? 'Full chart' : candidate}
            </Button>
          ))}
        </div>
        <div className="relative md:w-64">
          <Search className="text-muted-foreground/70 absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search symbols..."
            className="h-9 rounded-xl pl-9"
          />
        </div>
      </div>

      {isSearching ? (
        <div className="space-y-2">
          <h3 className="text-muted-foreground/85 text-xs font-semibold tracking-wider uppercase">
            Search Results ({searchResults.length})
          </h3>
          {searchResults.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7">
              {searchResults.map((symbol) => (
                <SymbolCell
                  key={symbol.soundTag}
                  symbol={symbol}
                  script={script}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground py-6 text-center text-sm">
              No matching pronunciation symbols found.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Initials Section */}
          {(view === 'full' || view === 'initials') && (
            <div className="space-y-3">
              <h3 className="text-muted-foreground/75 border-b pb-1 text-xs font-bold tracking-wider uppercase">
                Initials
              </h3>
              <div className="space-y-2">
                {initialsGroups.map((group, index) => (
                  <div key={index} className="flex flex-wrap gap-2">
                    {group.symbols.map((symbol) => (
                      <SymbolCell
                        key={symbol.soundTag}
                        symbol={symbol}
                        script={script}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Finals Section */}
          {(view === 'full' || view === 'finals') && (
            <div className="space-y-3">
              <h3 className="text-muted-foreground/75 border-b pb-1 text-xs font-bold tracking-wider uppercase">
                Finals
              </h3>
              <div className="space-y-2">
                {finalsGroups.map((group, index) => (
                  <div key={index} className="flex flex-wrap gap-2">
                    {group.symbols.map((symbol) => (
                      <SymbolCell
                        key={symbol.soundTag}
                        symbol={symbol}
                        script={script}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SymbolCell({
  symbol,
  script
}: {
  symbol: ChartSymbol
  script: PronunciationScript
}) {
  const sampleDetails = getSoundSample(symbol.soundTag)
  const primary = script === 'pinyin' ? symbol.pinyin : symbol.zhuyin
  const secondary = script === 'pinyin' ? symbol.zhuyin : symbol.pinyin

  const sampleLine = sampleDetails
    ? script === 'pinyin'
      ? `Sample: ${sampleDetails.samplePinyin} / ${sampleDetails.sampleZhuyin}`
      : `Sample: ${sampleDetails.sampleZhuyin} / ${sampleDetails.samplePinyin}`
    : ''

  return (
    <PronunciationAudioButton
      audioPath={sampleDetails?.audioPath}
      label={symbol.pinyin}
      className="flex max-w-full min-w-[140px] sm:w-[170px]"
    >
      {(status) => {
        const Icon = status === 'error' ? AlertCircle : Volume2
        return (
          <div
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-1.5 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
              status === 'playing'
                ? 'border-primary bg-primary/5 ring-primary/25 ring-2'
                : 'border-muted/40 bg-card/45 hover:bg-card/80',
              status === 'error' && 'opacity-60'
            )}
          >
            <div className="min-w-0 flex-1 text-left">
              <div className="flex items-baseline gap-1.5">
                <p
                  className={cn(
                    'text-foreground font-semibold tracking-wide',
                    script === 'zhuyin' ? 'text-2xl' : 'text-xl'
                  )}
                >
                  {primary}
                </p>
                <span className="text-muted-foreground text-[10px] font-medium">
                  ({secondary})
                </span>
              </div>
              {sampleLine && (
                <p className="text-muted-foreground mt-0.5 truncate text-[10px] leading-tight select-none">
                  {sampleLine}
                </p>
              )}
            </div>
            <div
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors',
                status === 'playing'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-muted-foreground/30 hover:border-muted-foreground/50'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </div>
          </div>
        )
      }}
    </PronunciationAudioButton>
  )
}
